import { ResponsiveValue } from "@shopify/restyle";
import React, { useState } from "react";
import { Pressable } from "react-native";

import { Box, HStack, Text, VStack } from "../../primitive";

type Align = "left" | "center" | "right";

export type SelectableTileProps = {
  testID?: string;
  isSmall?: boolean;
  leadingElement?: (
    isSelected?: boolean,
    isDisabled?: boolean
  ) => React.ReactNode;
  isSelected?: boolean;
  setSelected: (isSelected: boolean) => void;
  title: string;
  subtitle?: string;
  titleNumberOfLines?: number;
  isDisabled?: boolean;
  align?: Align;
  accessibilityLabel?: string;
  flexBasis?: ResponsiveValue<
    number | undefined | "auto",
    {
      base: number;
      xs: number;
      sm: number;
      md: number;
      agendaSplit: number;
      eventCardColumn: number;
      lg: number;
      xl: number;
    }
  >;
};

export const SelectableTile = ({
  testID,
  isSmall = false,
  leadingElement,
  isSelected = false,
  setSelected,
  title,
  subtitle,
  titleNumberOfLines = 1,
  isDisabled = false,
  align = "left",
  accessibilityLabel,
  flexBasis = "auto",
}: SelectableTileProps) => {
  const [isHovered, setHovered] = useState(false);
  const alignItems =
    align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center";

  const defaultLabel = subtitle ? `${title}, ${subtitle}` : title;

  return (
    <Box flexGrow={1} flexBasis={flexBasis} minHeight={48}>
      <Pressable
        testID={testID}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: isSelected, disabled: isDisabled }}
        aria-checked={isSelected}
        accessibilityLabel={accessibilityLabel ?? defaultLabel}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        onPress={() => (isDisabled ? null : setSelected(!isSelected))}
      >
        <HStack
          gap={4}
          px={isSmall ? 4 : 5}
          py={isSmall ? 3 : 5}
          borderWidth={isDisabled ? 0 : 1.5}
          borderColor={isSelected ? "outline-primary" : "grayscale-600"}
          backgroundColor={getBackgroundColor(
            isDisabled,
            isSelected,
            isHovered
          )}
          borderRadius={isSmall ? "2" : "4"}
          alignItems="center"
          justifyContent={alignItems}
        >
          {leadingElement ? leadingElement(isSelected, isDisabled) : null}
          <VStack gap={1} flexShrink={1}>
            <Text
              color={isDisabled ? "text-disabled" : "text-primary"}
              variant="mainBaseSemiBold"
              textAlign={align === "center" ? "center" : "left"}
              numberOfLines={titleNumberOfLines}
              ellipsizeMode="tail"
            >
              {title}
            </Text>
            {subtitle ? (
              <Text
                color={isDisabled ? "text-disabled" : "text-secondary"}
                variant="mainBaseMedium"
                textAlign={align === "center" ? "center" : "left"}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {subtitle}
              </Text>
            ) : null}
          </VStack>
        </HStack>
      </Pressable>
    </Box>
  );
};

const getBackgroundColor = (
  isDisabled: boolean,
  isSelected: boolean,
  isHovered: boolean
) => {
  if (isDisabled) return "surface-secondary";
  if (isSelected) return isHovered ? "grayscale-200" : "grayscale-100";
  return "surface-primary";
};
